import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HISTORY_IMPORT_SUPPORTED_PROVIDERS } from '@troia-v3/types';
import type { ChannelResponse } from '@/modules/channels/types';
import { CardHeader, CardContent } from '@/shared/components/ui/Card';
import { StatusPill, type StatusPillVariant } from '@/shared/components/ui/StatusPill';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { MemberAvatarList } from '@/shared/components/ui';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useAddUserToChannel, useRemoveUserFromChannel } from '../hooks';
import { WidgetSettingsModal } from './WidgetSettingsModal';
import { IntegrationCredentialsModal } from './IntegrationCredentialsModal';
import { ImportHistoryModal } from './import-history/ImportHistoryModal';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon, IntegrationIcon } from '@/shared/icons';
import { getChannelTypeMetadata, formatChannelIdentifier, formatPlatform } from '../utils/channelTypeMetadata';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import { resolveDisconnectReason } from '../constants/disconnectReasons';

export interface ChannelCardProps {
  channel: ChannelResponse;
  members?: Array<{ id: string; name: string; avatar?: string }>;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleStatus?: (id: string, currentStatus: string) => void;
  onReconnect?: (id: string) => void;
}

/** Extrai iniciais para AvatarFallback. Usa pushName se houver, senão nome do canal. */
function getInitials(displayName: string | undefined, fallbackName: string | undefined): string {
  const source = displayName?.trim() || fallbackName?.trim() || '';
  if (!source) return '?';
  const parts = source.split(/\s+/);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return (parts[0] ?? '').slice(0, 2).toUpperCase() || '?';
}

/** Badge do provider — pequeno círculo branco com logo da marca sobreposto ao avatar. */
const ProviderBadge: React.FC<{ providerId: string }> = ({ providerId }) => (
  <div
    className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full"
    style={{
      width: 18,
      height: 18,
      backgroundColor: '#ffffff',
      border: '2px solid #ffffff',
      boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
    }}
    aria-hidden
  >
    <IntegrationIcon id={providerId} size={11} variant="brand" />
  </div>
);

export const ChannelCard: React.FC<ChannelCardProps> = ({
  channel,
  members = [],
  onDelete,
  onReconnect,
}) => {
  const navigate = useNavigate();
  const [showWidgetModal, setShowWidgetModal] = React.useState(false);
  const [showIntegrationModal, setShowIntegrationModal] = React.useState(false);
  const [showImportModal, setShowImportModal] = React.useState(false);

  const [localMembers, setLocalMembers] = React.useState(members);
  React.useEffect(() => { setLocalMembers(members); }, [members]);

  const { data: allUsers = [], isLoading: loadingUsers } = useUsersDropdown({ status: 'active' });
  const addUserMutation = useAddUserToChannel();
  const removeUserMutation = useRemoveUserFromChannel();
  const availableUsers = allUsers;

  const handleAddMember = (userId: string) => {
    const newMember = availableUsers.find(u => u.id === userId);
    if (newMember) setLocalMembers([...localMembers, newMember]);
    addUserMutation.mutate({ channelId: channel.id, userId, role: 'attendant', priority: 1 });
  };

  const handleRemoveMember = (userId: string) => {
    setLocalMembers(localMembers.filter(m => m.id !== userId));
    removeUserMutation.mutate({ channelId: channel.id, userId });
  };

  // Actions do menu
  const actions: CardAction[] = [
    { label: 'Configurações', icon: 'settings', onClick: () => navigate(`/settings/channels/${channel.id}/edit`) },
    { label: 'Dashboard', icon: 'bar-chart', onClick: () => navigate(`/settings/channels/${channel.id}/dashboard`) },
  ];
  if (channel.providerId === 'website-widget') {
    actions.push({ label: 'Incorporação', icon: 'code', onClick: () => setShowWidgetModal(true) });
  }
  if (channel.integration?.instanceKey) {
    actions.push({ label: 'Integração', icon: 'key', onClick: () => setShowIntegrationModal(true) });
  }
  if (channel.providerId && HISTORY_IMPORT_SUPPORTED_PROVIDERS.includes(channel.providerId) && channel.status === 'active') {
    actions.push({ label: 'Importar histórico', icon: 'download', onClick: () => setShowImportModal(true) });
  }
  if (onReconnect) {
    actions.push({ label: 'Reconectar', icon: 'refresh', onClick: () => onReconnect(channel.id) });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(channel.id), variant: 'danger' });
  }

  // Status — StatusPill (F62): verde=ligado, âmbar=aguardando, neutro=inativo
  const disconnected = channel.status !== 'active' && channel.status !== 'pending';
  const statusVariant: StatusPillVariant =
    channel.status === 'active' ? 'success' : channel.status === 'pending' ? 'warning' : 'neutral';
  const statusLabel = channel.status === 'active'
    ? 'Ativo'
    : channel.status === 'pending'
      ? 'Aguardando'
      : 'Inativo';

  // Dados da conta conectada (preenchido via webhook account.updated)
  const accountInfo = channel.accountInfo;
  const pushName = accountInfo?.businessName || accountInfo?.verifiedName || accountInfo?.pushName;
  const platform = formatPlatform(accountInfo?.platform);

  // Metadata do tipo de canal
  const meta = getChannelTypeMetadata(channel.providerId);
  const formattedIdentifier = formatChannelIdentifier(channel.providerId, channel.identifier);

  // Rodapé: device se houver, senão fallback do metadata (ex: "Meta Business")
  const footerLabel = platform ?? meta.defaultFooter;

  // Métricas: vêm populadas do backend em channel.stats
  const stats = channel.stats;
  const metricMessages = stats?.messagesLast7Days != null ? String(stats.messagesLast7Days) : '—';
  const metricConversations = stats?.conversations != null ? String(stats.conversations) : '—';
  const metricOperators = localMembers.length > 0 ? String(localMembers.length) : '—';

  return (
    <>
      {channel.providerId === 'website-widget' && (
        <WidgetSettingsModal
          isOpen={showWidgetModal}
          onClose={() => setShowWidgetModal(false)}
          channel={channel}
        />
      )}
      <IntegrationCredentialsModal
        isOpen={showIntegrationModal}
        onClose={() => setShowIntegrationModal(false)}
        instanceKey={channel.integration?.instanceKey || null}
        instanceToken={channel.integration?.instanceToken || null}
        channelName={channel.name}
      />

      <ImportHistoryModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        channelId={channel.id}
      />

      {/* Card flat (F33): rounded-xl + borda, sem sombra; sem checkbox (F34) */}
      <div
        className="relative rounded-xl border transition-colors"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
          color: 'var(--theme-card-text)',
        }}
        data-testid={`channel-card-${channel.id}`}
      >
        {/* ════ Header: avatar + nome + status dot + actions ════ */}
        <CardHeader className="pb-2 py-3 px-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-shrink-0">
              <Avatar className="h-11 w-11">
                {accountInfo?.profilePictureUrl && (
                  <AvatarImage src={accountInfo.profilePictureUrl} alt={channel.name} />
                )}
                <AvatarFallback
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
                    color: '#ffffff',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {getInitials(pushName, channel.name)}
                </AvatarFallback>
              </Avatar>
              <ProviderBadge providerId={channel.providerId || 'unknown'} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3
                  className="text-sm font-semibold truncate"
                  style={{ color: 'var(--theme-card-text)' }}
                  data-testid={`channel-card-name-${channel.id}`}
                >
                  {channel.name}
                </h3>
                {(() => {
                  const disconnectInfo = channel.disconnectInfo;
                  const showTooltip =
                    (channel.status === 'error' ||
                      channel.status === 'suspended' ||
                      channel.status === 'pending') &&
                    !!disconnectInfo?.reason;
                  const friendly = disconnectInfo
                    ? resolveDisconnectReason(disconnectInfo)
                    : '';
                  const dotColor =
                    channel.status === 'active'
                      ? '#22c55e'
                      : channel.status === 'pending'
                        ? '#f59e0b'
                        : channel.status === 'error' || channel.status === 'suspended'
                          ? '#ef4444'
                          : '#cbd5e1';

                  const dot = (
                    <span
                      className={`inline-block flex-shrink-0 rounded-full${showTooltip ? ' cursor-help' : ''}`}
                      style={{ width: 8, height: 8, backgroundColor: dotColor }}
                      aria-label={statusLabel}
                      data-testid={`channel-card-status-dot-${channel.id}`}
                    />
                  );

                  return showTooltip ? (
                    <Tooltip
                      side="top"
                      content={
                        <div
                          data-testid={`channel-card-status-tooltip-${channel.id}`}
                          className="max-w-[240px]"
                        >
                          <div className="font-semibold mb-0.5">Desconectado</div>
                          <div className="font-normal opacity-90">{friendly}</div>
                        </div>
                      }
                    >
                      {dot}
                    </Tooltip>
                  ) : dot;
                })()}
                {channel.integration?.accountMode === 'COEXISTENCE' && (
                  <span
                    className="px-2 py-0.5 rounded text-xs font-medium flex-shrink-0"
                    style={{
                      backgroundColor: 'rgba(139, 92, 246, 0.15)',
                      color: 'var(--theme-primary)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                    }}
                    data-testid={`channel-card-coexistence-badge-${channel.id}`}
                    title="Este número está em modo Coexistência — também ativo no WhatsApp Business App"
                  >
                    Coexistência
                  </span>
                )}
              </div>
              {pushName && (
                <p className="text-xs truncate mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                  {pushName}
                </p>
              )}
            </div>

            <CardActionsMenu actions={actions} size="sm" data-testid={`channel-card-actions-${channel.id}`} />
          </div>
        </CardHeader>

        {/* ════ Bloco de identificador (gradient sutil — funciona em light/dark via rgba) ════ */}
        {channel.identifier && (
          <div
            className="px-4 py-2.5 flex items-center gap-2.5"
            style={{
              background: `linear-gradient(90deg, ${meta.accentColor}14, transparent)`,
            }}
          >
            <div className="flex-shrink-0">
              <IntegrationIcon id={channel.providerId || 'unknown'} size={20} variant="brand-soft" />
            </div>
            <div className="min-w-0 flex-1">
              <div
                className="uppercase tracking-wide"
                style={{ fontSize: 10, color: 'var(--theme-muted-text)', letterSpacing: '0.04em' }}
              >
                {meta.identifierLabel}
              </div>
              <div
                className="font-semibold truncate"
                style={{ fontSize: 14, color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
              >
                {formattedIdentifier || channel.identifier}
              </div>
            </div>
          </div>
        )}

        {/* ════ Métricas: 3 mini-cards ════ */}
        <CardContent className="px-4 py-3">
          <div className="grid grid-cols-3 gap-2 mb-3">
            <StatCard label={meta.messagesLabel} value={metricMessages} />
            <StatCard label={meta.conversationsLabel} value={metricConversations} />
            <StatCard label="Operadores" value={metricOperators} />
          </div>

          {/* Operadores — mantém shared MemberAvatarList (dropdown para adicionar) */}
          <div className="mb-2">
            <MemberAvatarList
              members={localMembers}
              availableUsers={availableUsers}
              limit={4}
              onAddMember={handleAddMember}
              onRemoveMember={handleRemoveMember}
              canRemoveMembers={false}
              loading={loadingUsers}
              placeholder="Buscar operador..."
              emptyMessage="Sem operadores"
            />
          </div>

          {/* ════ Rodapé: reconectar (desconectado) ou device + status pill ════ */}
          <div className="flex items-center justify-between mt-2">
            {onReconnect && disconnected ? (
              <button
                type="button"
                onClick={() => onReconnect(channel.id)}
                className="inline-flex items-center gap-1.5 rounded-lg transition-colors"
                style={{
                  height: 28,
                  padding: '0 10px',
                  border: '1px solid var(--theme-primary)',
                  color: 'var(--theme-primary)',
                  fontSize: 12,
                  fontWeight: 500,
                }}
                data-testid={`channel-card-reconnect-${channel.id}`}
              >
                <Icon name="refresh" className="w-3.5 h-3.5" />
                Reconectar
              </button>
            ) : (
              <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                {footerLabel}
              </span>
            )}
            <StatusPill variant={statusVariant} label={statusLabel} data-testid={`channel-card-status-${channel.id}`} />
          </div>
        </CardContent>
      </div>
    </>
  );
};

ChannelCard.displayName = 'ChannelCard';

/** Mini card de métrica — background sutil com rgba para funcionar em light/dark. */
const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div
    className="rounded-lg text-center py-2 px-1.5"
    style={{ backgroundColor: 'rgba(127, 127, 127, 0.06)' }}
  >
    <div
      className="font-bold leading-tight"
      style={{ fontSize: 15, color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </div>
    <div
      className="uppercase tracking-wider mt-0.5"
      style={{ fontSize: 9, color: 'var(--theme-muted-text)', letterSpacing: '0.05em' }}
    >
      {label}
    </div>
  </div>
);
